import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Stage, Layer, Text, Image as KonvaImage, Transformer, Rect } from 'react-konva';
import useImage from 'use-image';
import QRCode from 'qrcode';
import { X, ArrowCounterClockwise, ArrowClockwise, TextT, QrCode, ImageSquare, DownloadSimple, Trash, Lock, LockOpen, Eye, EyeSlash, ArrowUp, ArrowDown, FloppyDisk, MagicWand, Palette } from '@phosphor-icons/react';

const CANVAS_W = 600;
const CANVAS_H = 900;

const GOOGLE_FONTS = [
  'Inter', 'Playfair Display', 'Montserrat', 'Poppins', 'Raleway',
  'Oswald', 'Lora', 'Roboto Slab', 'Dancing Script', 'Pacifico',
  'Great Vibes', 'Bebas Neue', 'Quicksand', 'Cormorant Garamond'
];

const CX = CANVAS_W / 2; // center x for alignment

const TEMPLATES = [
  {
    id: 'wedding-purple',
    name: 'Wedding Purple',
    preview: 'linear-gradient(135deg, #2d1b69, #B89762)',
    elements: [
      { id: 'bg', type: 'rect', x: 0, y: 0, width: CANVAS_W, height: CANVAS_H, fill: '#1a0a3e' },
      { id: 'h1', type: 'text', x: CX, y: 60, text: "Let's Celebrate", fontSize: 32, fontFamily: 'Great Vibes', fill: '#d4b8ff', draggable: true, align: 'center', offsetX: true },
      { id: 'h2', type: 'text', x: CX, y: 120, text: "Your Event Name", fontSize: 52, fontFamily: 'Playfair Display', fill: '#ffffff', draggable: true, align: 'center', offsetX: true },
      { id: 'h3', type: 'text', x: CX, y: 200, text: "18/04/2026", fontSize: 24, fontFamily: 'Inter', fill: '#c9a0ff', draggable: true, align: 'center', offsetX: true },
      { id: 'h4', type: 'text', x: CX, y: 250, text: "Share all your photos.\nCreate everlasting memories.", fontSize: 20, fontFamily: 'Inter', fill: '#e0d0ff', draggable: true, align: 'center', offsetX: true },
      { id: 'qr_frame', type: 'rect', x: CX - 145, y: 340, width: 290, height: 290, fill: '#ffffff', stroke: '#c9a96e', strokeWidth: 4, draggable: true },
      { id: 'tqr', type: 'qr', x: CX - 125, y: 360, width: 250, height: 250, draggable: true },
      { id: 'cta', type: 'text', x: CX, y: 670, text: "SCAN  •  UPLOAD  •  SHARE", fontSize: 18, fontFamily: 'Bebas Neue', fill: '#ffffff', draggable: true, align: 'center', offsetX: true },
      { id: 'brand', type: 'text', x: CX, y: 830, text: "Powered by EventDrop.co", fontSize: 14, fontFamily: 'Inter', fill: '#8866cc', draggable: true, align: 'center', offsetX: true },
    ]
  },
  {
    id: 'birthday-white',
    name: 'Birthday Elegant',
    preview: 'linear-gradient(135deg, #f5f0e8, #e8dcc8)',
    elements: [
      { id: 'bg', type: 'rect', x: 0, y: 0, width: CANVAS_W, height: CANVAS_H, fill: '#f5f0e8' },
      { id: 'h1', type: 'text', x: CX, y: 60, text: "Let's Celebrate", fontSize: 32, fontFamily: 'Great Vibes', fill: '#5c3d2e', draggable: true, align: 'center', offsetX: true },
      { id: 'h2', type: 'text', x: CX, y: 120, text: "Your Event Name", fontSize: 52, fontFamily: 'Playfair Display', fill: '#3a2218', draggable: true, align: 'center', offsetX: true },
      { id: 'h4', type: 'text', x: CX, y: 220, text: "Share all your photos.\nCreate everlasting memories.", fontSize: 20, fontFamily: 'Inter', fill: '#7a6555', draggable: true, align: 'center', offsetX: true },
      { id: 'qr_frame', type: 'rect', x: CX - 145, y: 320, width: 290, height: 290, fill: '#ffffff', stroke: '#c9a96e', strokeWidth: 4, draggable: true },
      { id: 'tqr', type: 'qr', x: CX - 125, y: 340, width: 250, height: 250, draggable: true },
      { id: 'cta', type: 'text', x: CX, y: 650, text: "SCAN  •  UPLOAD  •  SHARE", fontSize: 18, fontFamily: 'Bebas Neue', fill: '#3a2218', draggable: true, align: 'center', offsetX: true },
      { id: 'brand', type: 'text', x: CX, y: 830, text: "Powered by EventDrop.co", fontSize: 14, fontFamily: 'Inter', fill: '#a08878', draggable: true, align: 'center', offsetX: true },
    ]
  },
  {
    id: 'modern-dark',
    name: 'Modern Dark',
    preview: 'linear-gradient(135deg, #15161B, #15161B)',
    elements: [
      { id: 'bg', type: 'rect', x: 0, y: 0, width: CANVAS_W, height: CANVAS_H, fill: '#15161B' },
      { id: 'line1', type: 'rect', x: 0, y: 0, width: CANVAS_W, height: 6, fill: '#B89762' },
      { id: 'h1', type: 'text', x: CX, y: 50, text: "Let's Celebrate", fontSize: 30, fontFamily: 'Dancing Script', fill: '#C29D52', draggable: true, align: 'center', offsetX: true },
      { id: 'h2', type: 'text', x: CX, y: 110, text: "Your Event Name", fontSize: 48, fontFamily: 'Montserrat', fill: '#ffffff', draggable: true, align: 'center', offsetX: true },
      { id: 'h3', type: 'text', x: CX, y: 185, text: "18/04/2026", fontSize: 20, fontFamily: 'Inter', fill: '#B89762', draggable: true, align: 'center', offsetX: true },
      { id: 'h4', type: 'text', x: CX, y: 230, text: "Share all your photos.\nCreate everlasting memories.", fontSize: 18, fontFamily: 'Inter', fill: '#8888aa', draggable: true, align: 'center', offsetX: true },
      { id: 'qr_frame', type: 'rect', x: CX - 145, y: 320, width: 290, height: 290, fill: '#15161B', stroke: '#C29D52', strokeWidth: 2, draggable: true },
      { id: 'tqr', type: 'qr', x: CX - 125, y: 340, width: 250, height: 250, draggable: true },
      { id: 'cta', type: 'text', x: CX, y: 650, text: "SCAN  •  UPLOAD  •  SHARE", fontSize: 18, fontFamily: 'Bebas Neue', fill: '#C29D52', draggable: true, align: 'center', offsetX: true },
      { id: 'line2', type: 'rect', x: 0, y: CANVAS_H - 6, width: CANVAS_W, height: 6, fill: '#C29D52' },
      { id: 'brand', type: 'text', x: CX, y: 830, text: "Powered by EventDrop.co", fontSize: 14, fontFamily: 'Inter', fill: '#555577', draggable: true, align: 'center', offsetX: true },
    ]
  },
  {
    id: 'party-neon',
    name: 'Party Neon',
    preview: 'linear-gradient(135deg, #0a0a0a, #1a0030)',
    elements: [
      { id: 'bg', type: 'rect', x: 0, y: 0, width: CANVAS_W, height: CANVAS_H, fill: '#0a0a0a' },
      { id: 'h1', type: 'text', x: CX, y: 60, text: "Let's Party", fontSize: 36, fontFamily: 'Pacifico', fill: '#FF00FF', draggable: true, align: 'center', offsetX: true },
      { id: 'h2', type: 'text', x: CX, y: 120, text: "YOUR EVENT", fontSize: 56, fontFamily: 'Bebas Neue', fill: '#00FFDD', draggable: true, align: 'center', offsetX: true },
      { id: 'h4', type: 'text', x: CX, y: 220, text: "Share all your photos.\nCreate everlasting memories.", fontSize: 18, fontFamily: 'Poppins', fill: '#FF6B6B', draggable: true, align: 'center', offsetX: true },
      { id: 'qr_frame', type: 'rect', x: CX - 145, y: 320, width: 290, height: 290, fill: '#111111', stroke: '#FF00FF', strokeWidth: 3, draggable: true },
      { id: 'tqr', type: 'qr', x: CX - 125, y: 340, width: 250, height: 250, draggable: true },
      { id: 'cta', type: 'text', x: CX, y: 650, text: "SCAN  •  UPLOAD  •  SHARE", fontSize: 18, fontFamily: 'Bebas Neue', fill: '#FF00FF', draggable: true, align: 'center', offsetX: true },
      { id: 'brand', type: 'text', x: CX, y: 830, text: "Powered by EventDrop.co", fontSize: 14, fontFamily: 'Inter', fill: '#555555', draggable: true, align: 'center', offsetX: true },
    ]
  },
  {
    id: 'free-canvas',
    name: 'Free Design',
    preview: 'linear-gradient(135deg, #222, #444)',
    elements: [
      { id: 'bg', type: 'rect', x: 0, y: 0, width: CANVAS_W, height: CANVAS_H, fill: '#ffffff' },
      { id: 'tqr', type: 'qr', x: CX - 125, y: CX - 125, width: 250, height: 250, draggable: true },
    ]
  }
];

// Load Google Fonts
const loadFont = (font) => {
  const link = document.createElement('link');
  link.href = `https://fonts.googleapis.com/css2?family=${font.replace(/ /g, '+')}&display=swap`;
  link.rel = 'stylesheet';
  if (!document.querySelector(`link[href="${link.href}"]`)) {
    document.head.appendChild(link);
  }
};

// URL Image component for Konva
const URLImage = ({ el, isSelected, onSelect, onChange }) => {
  const [image] = useImage(el.src, 'anonymous');
  return (
    <KonvaImage
      id={el.id}
      image={image}
      x={el.x} y={el.y}
      width={el.width} height={el.height}
      draggable={el.draggable !== false}
      onClick={onSelect}
      onTap={onSelect}
      onDragEnd={(e) => onChange({ x: e.target.x(), y: e.target.y() })}
      onTransformEnd={(e) => {
        const node = e.target;
        onChange({
          x: node.x(), y: node.y(),
          width: Math.max(20, node.width() * node.scaleX()),
          height: Math.max(20, node.height() * node.scaleY()),
          scaleX: 1, scaleY: 1
        });
      }}
    />
  );
};

const QRDesignEditor = ({ eventUrl, eventName, onClose, onSave, savedDesign }) => {
  const stageRef = useRef(null);
  const transformerRef = useRef(null);
  const containerRef = useRef(null);
  const mobileContainerRef = useRef(null);

  const [elements, setElements] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [history, setHistory] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [qrDataUrl, setQrDataUrl] = useState(null);
  const [stageScale, setStageScale] = useState(1);
  const [activePanel, setActivePanel] = useState('templates');
  const [editingTextId, setEditingTextId] = useState(null);

  // Generate QR code data URL
  useEffect(() => {
    QRCode.toDataURL(eventUrl, { width: 400, margin: 1, color: { dark: '#000000', light: '#ffffff' } })
      .then(setQrDataUrl);
  }, [eventUrl]);

  // Load Google Fonts
  useEffect(() => {
    GOOGLE_FONTS.forEach(loadFont);
  }, []);

  // Load saved design or default template
  useEffect(() => {
    if (savedDesign && savedDesign.length > 0) {
      const restored = savedDesign.map(el => {
        if (el.type === 'qr' && qrDataUrl) return { ...el, src: qrDataUrl };
        return el;
      });
      setElements(restored);
    } else {
      applyTemplate(TEMPLATES[0]);
    }
  }, [qrDataUrl]);

  // Responsive canvas scaling
  useEffect(() => {
    const resize = () => {
      const isMobile = window.innerWidth < 768;
      const el = isMobile ? mobileContainerRef.current : containerRef.current;
      if (!el) return;
      const cw = el.offsetWidth - 16;
      const ch = el.offsetHeight - 16;
      const scaleW = cw / CANVAS_W;
      const scaleH = ch / CANVAS_H;
      const scale = Math.min(scaleW, scaleH, 1);
      setStageScale(Math.max(scale, 0.15));
    };
    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);

  // Attach transformer to selected node
  useEffect(() => {
    if (!transformerRef.current || !stageRef.current) return;
    if (selectedId) {
      const node = stageRef.current.findOne('#' + selectedId);
      if (node) {
        transformerRef.current.nodes([node]);
        transformerRef.current.getLayer().batchDraw();
      }
    } else {
      transformerRef.current.nodes([]);
      transformerRef.current.getLayer().batchDraw();
    }
  }, [selectedId, elements]);

  const pushHistory = useCallback((newEls) => {
    setHistory(prev => [...prev, elements]);
    setElements(newEls);
    setRedoStack([]);
  }, [elements]);

  const undo = () => {
    if (!history.length) return;
    setRedoStack([elements, ...redoStack]);
    setElements(history[history.length - 1]);
    setHistory(history.slice(0, -1));
  };

  const redo = () => {
    if (!redoStack.length) return;
    setHistory([...history, elements]);
    setElements(redoStack[0]);
    setRedoStack(redoStack.slice(1));
  };

  const updateElement = (id, attrs) => {
    pushHistory(elements.map(el => el.id === id ? { ...el, ...attrs } : el));
  };

  const deleteElement = () => {
    if (!selectedId) return;
    pushHistory(elements.filter(el => el.id !== selectedId));
    setSelectedId(null);
  };

  const addText = () => {
    const el = {
      id: 'text_' + Date.now(),
      type: 'text', x: 100, y: 200,
      text: 'Your text here', fontSize: 28,
      fontFamily: 'Inter', fill: '#ffffff', draggable: true
    };
    pushHistory([...elements, el]);
    setSelectedId(el.id);
  };

  const addQR = () => {
    if (!qrDataUrl) return;
    const el = {
      id: 'qr_' + Date.now(),
      type: 'qr', x: 300, y: 150,
      src: qrDataUrl, width: 200, height: 200, draggable: true
    };
    pushHistory([...elements, el]);
    setSelectedId(el.id);
  };

  const handleBgUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const bgEl = {
        id: 'bg_' + Date.now(),
        type: 'image', x: 0, y: 0,
        src: reader.result, width: CANVAS_W, height: CANVAS_H, draggable: false
      };
      // Put background at position 0
      pushHistory([bgEl, ...elements.filter(el => !el.id.startsWith('bg_'))]);
    };
    reader.readAsDataURL(file);
  };

  const applyTemplate = (template) => {
    const els = template.elements.map(el => {
      if (el.type === 'qr' && qrDataUrl) return { ...el, src: qrDataUrl };
      return { ...el };
    });
    pushHistory(els);
    setSelectedId(null);
  };

  const moveLayer = (dir) => {
    if (!selectedId) return;
    const idx = elements.findIndex(el => el.id === selectedId);
    if (idx < 0) return;
    const arr = [...elements];
    const newIdx = dir === 'up' ? Math.min(idx + 1, arr.length - 1) : Math.max(idx - 1, 0);
    if (newIdx === idx) return;
    [arr[idx], arr[newIdx]] = [arr[newIdx], arr[idx]];
    pushHistory(arr);
  };

  const exportImage = () => {
    if (!stageRef.current) return;
    // Deselect first for clean export
    setSelectedId(null);
    setTimeout(() => {
      const uri = stageRef.current.toDataURL({ pixelRatio: 3 });
      const link = document.createElement('a');
      link.download = `${eventName || 'event'}-qr-design.png`;
      link.href = uri;
      link.click();
    }, 100);
  };

  const handleSave = () => {
    // Strip src from QR elements (regenerated on load)
    const toSave = elements.map(el => {
      if (el.type === 'qr') { const { src, ...rest } = el; return rest; }
      return el;
    });
    onSave(toSave);
  };

  const handleStageClick = (e) => {
    if (e.target === e.target.getStage() || e.target.attrs.id === 'canvas-bg') {
      setSelectedId(null);
      setEditingTextId(null);
    }
  };

  const handleDblClick = (e) => {
    const id = e.target.attrs.id;
    const el = elements.find(x => x.id === id);
    if (el?.type === 'text') {
      setEditingTextId(id);
      // Create textarea overlay for inline editing
      const textNode = e.target;
      const tr = transformerRef.current;
      if (tr) tr.nodes([]);
      textNode.hide();

      const textPosition = textNode.absolutePosition();
      const stage = stageRef.current;
      const container = stage.container();
      const areaPosition = { x: container.offsetLeft + textPosition.x * stageScale, y: container.offsetTop + textPosition.y * stageScale };

      const textarea = document.createElement('textarea');
      container.parentNode.appendChild(textarea);
      textarea.value = el.text;
      textarea.style.position = 'absolute';
      textarea.style.top = areaPosition.y + 'px';
      textarea.style.left = areaPosition.x + 'px';
      textarea.style.width = (textNode.width() * stageScale) + 20 + 'px';
      textarea.style.fontSize = (el.fontSize * stageScale) + 'px';
      textarea.style.fontFamily = el.fontFamily;
      textarea.style.color = el.fill;
      textarea.style.background = 'transparent';
      textarea.style.border = '2px solid #B89762';
      textarea.style.borderRadius = '4px';
      textarea.style.padding = '2px 4px';
      textarea.style.outline = 'none';
      textarea.style.resize = 'none';
      textarea.style.zIndex = '1000';
      textarea.style.lineHeight = '1.2';
      textarea.focus();

      const removeTextarea = () => {
        updateElement(id, { text: textarea.value });
        textarea.remove();
        textNode.show();
        setEditingTextId(null);
      };

      textarea.addEventListener('blur', removeTextarea);
      textarea.addEventListener('keydown', (ev) => {
        if (ev.key === 'Enter' && !ev.shiftKey) { ev.preventDefault(); removeTextarea(); }
        if (ev.key === 'Escape') removeTextarea();
      });
    }
  };

  // Panel content renderer (shared between desktop sidebar and mobile sheet)
  const selectedEl = elements.find(el => el.id === selectedId);

  const renderPanelContent = () => {
    if (!activePanel) return null;
    return (
      <>
        {activePanel === 'templates' && (
          <div>
            <h3 className="text-white text-sm font-bold mb-3">Templates</h3>
            <div className="grid grid-cols-2 md:grid-cols-1 gap-2">
              {TEMPLATES.map(t => (
                <button key={t.id} onClick={() => { applyTemplate(t); if (window.innerWidth < 768) setActivePanel(null); }}
                  className="w-full p-3 rounded-xl border border-white/10 hover:border-[#C29D52] transition-all text-left group"
                  data-testid={`template-${t.id}`}>
                  <div className="w-full h-12 rounded-lg mb-2" style={{ background: t.preview }} />
                  <p className="text-white text-xs font-semibold group-hover:text-[#C29D52]">{t.name}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {activePanel === 'text' && (
          <div>
            <h3 className="text-white text-sm font-bold mb-3">Add Text</h3>
            <button onClick={() => { addText(); if (window.innerWidth < 768) setActivePanel(null); }} className="w-full py-3 rounded-xl text-sm font-bold text-white bg-[#B89762]/20 hover:bg-[#B89762]/85/30 border border-[#B89762]/50/30 transition-all mb-4" data-testid="add-text-btn">
              + Add Text
            </button>
            <h4 className="text-gray-400 text-xs font-semibold mb-2 uppercase">Quick Styles</h4>
            {[
              { text: 'Heading', fontSize: 42, fontFamily: 'Playfair Display', fill: '#ffffff' },
              { text: 'Subheading', fontSize: 24, fontFamily: 'Inter', fill: '#cccccc' },
              { text: 'Body Text', fontSize: 16, fontFamily: 'Inter', fill: '#999999' },
              { text: 'Script', fontSize: 36, fontFamily: 'Dancing Script', fill: '#c9a96e' },
            ].map((style, i) => (
              <button key={i} onClick={() => {
                const el = { id: 'text_' + Date.now(), type: 'text', x: 80, y: 100 + i * 60, draggable: true, ...style };
                pushHistory([...elements, el]);
                setSelectedId(el.id);
                if (window.innerWidth < 768) setActivePanel(null);
              }} className="w-full py-2 px-3 rounded-lg text-left hover:bg-white/5 transition-all mb-1" style={{ fontFamily: style.fontFamily, fontSize: Math.min(style.fontSize * 0.6, 20), color: style.fill }}>
                {style.text}
              </button>
            ))}
          </div>
        )}

        {activePanel === 'qr' && (
          <div>
            <h3 className="text-white text-sm font-bold mb-3">QR Code</h3>
            <p className="text-gray-400 text-xs mb-3 break-all">{eventUrl}</p>
            <button onClick={() => { addQR(); if (window.innerWidth < 768) setActivePanel(null); }} className="w-full py-3 rounded-xl text-sm font-bold text-white bg-[#C29D52]/20 hover:bg-[#C29D52]/30 border border-[#B89762]/40/30 transition-all" data-testid="add-qr-btn">
              + Add QR Code
            </button>
            {qrDataUrl && (
              <div className="mt-4 bg-white rounded-lg p-3 flex items-center justify-center">
                <img src={qrDataUrl} alt="QR Preview" className="w-24 h-24 md:w-32 md:h-32" />
              </div>
            )}
          </div>
        )}

        {activePanel === 'bg' && (
          <div>
            <h3 className="text-white text-sm font-bold mb-3">Background</h3>
            <label className="block w-full py-3 rounded-xl text-sm font-bold text-white bg-white/10 hover:bg-white/15 border border-white/10 transition-all text-center cursor-pointer" data-testid="upload-bg-btn">
              Upload Image
              <input type="file" accept="image/*" className="hidden" onChange={handleBgUpload} />
            </label>
            <h4 className="text-gray-400 text-xs font-semibold mt-4 mb-2 uppercase">Solid Colors</h4>
            <div className="grid grid-cols-6 md:grid-cols-4 gap-2">
              {['#15161B', '#1a1a2e', '#ffffff', '#000000', '#B89762', '#C29D52', '#FF0099', '#c9a96e', '#1B5E20', '#B71C1C', '#0D47A1', '#F57F17'].map(c => (
                <button key={c} onClick={() => {
                  const bgRect = { id: 'bg_solid', type: 'rect', x: 0, y: 0, width: CANVAS_W, height: CANVAS_H, fill: c, draggable: false };
                  pushHistory([bgRect, ...elements.filter(el => !el.id.startsWith('bg_'))]);
                  if (window.innerWidth < 768) setActivePanel(null);
                }} className="w-10 h-10 rounded-lg border border-white/10 hover:border-white/40 transition-all" style={{ background: c }} />
              ))}
            </div>
          </div>
        )}

        {activePanel === 'layers' && (
          <div>
            <h3 className="text-white text-sm font-bold mb-3">Layers</h3>
            <div className="space-y-1">
              {[...elements].reverse().map(el => (
                <div key={el.id} onClick={() => { setSelectedId(el.id); if (window.innerWidth < 768) setActivePanel(null); }}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer text-xs transition-all ${selectedId === el.id ? 'bg-[#B89762]/20 text-[#C29D52]' : 'text-gray-400 hover:bg-white/5 hover:text-white'}`}>
                  <span className="flex-1 truncate">{el.type === 'text' ? el.text?.slice(0, 20) : el.type === 'qr' ? 'QR Code' : el.type === 'rect' ? 'Rectangle' : 'Image'}</span>
                  <span className="text-[10px] text-gray-600 uppercase">{el.type}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {activePanel === 'properties' && selectedEl && (
          <div className="space-y-4">
            <h3 className="text-white text-sm font-bold">Properties</h3>
            <div className="flex gap-2">
              <button onClick={() => { deleteElement(); setActivePanel(null); }} className="flex-1 py-2 rounded-lg text-xs font-semibold text-red-400 bg-red-500/10 hover:bg-red-500/20 transition-all" data-testid="delete-element">
                <Trash size={14} weight="bold" className="inline mr-1" /> Delete
              </button>
              <button onClick={() => moveLayer('up')} className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-all" title="Move up">
                <ArrowUp size={14} weight="bold" />
              </button>
              <button onClick={() => moveLayer('down')} className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-all" title="Move down">
                <ArrowDown size={14} weight="bold" />
              </button>
            </div>
            {selectedEl.type === 'text' && (
              <>
                <div>
                  <label className="text-gray-400 text-xs mb-1 block">Text</label>
                  <textarea value={selectedEl.text} onChange={(e) => updateElement(selectedId, { text: e.target.value })}
                    className="w-full px-3 py-2 text-sm rounded-lg bg-white/5 border border-white/10 text-white resize-none focus:border-[#B89762]/50 focus:outline-none"
                    rows={2} data-testid="prop-text" />
                </div>
                <div>
                  <label className="text-gray-400 text-xs mb-1 block">Font</label>
                  <select value={selectedEl.fontFamily} onChange={(e) => updateElement(selectedId, { fontFamily: e.target.value })}
                    className="w-full px-3 py-2 text-sm rounded-lg bg-white/5 border border-white/10 text-white focus:border-[#B89762]/50 focus:outline-none"
                    data-testid="prop-font">
                    {GOOGLE_FONTS.map(f => <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>)}
                  </select>
                </div>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <label className="text-gray-400 text-xs mb-1 block">Size</label>
                    <input type="number" value={selectedEl.fontSize} onChange={(e) => updateElement(selectedId, { fontSize: Number(e.target.value) })}
                      className="w-full px-3 py-2 text-sm rounded-lg bg-white/5 border border-white/10 text-white focus:border-[#B89762]/50 focus:outline-none"
                      min={8} max={120} data-testid="prop-size" />
                  </div>
                  <div className="flex-1">
                    <label className="text-gray-400 text-xs mb-1 block">Color</label>
                    <input type="color" value={selectedEl.fill} onChange={(e) => updateElement(selectedId, { fill: e.target.value })}
                      className="w-full h-[38px] rounded-lg cursor-pointer border border-white/10" data-testid="prop-color" />
                  </div>
                </div>
              </>
            )}
            {selectedEl.type === 'rect' && (
              <div>
                <label className="text-gray-400 text-xs mb-1 block">Fill Color</label>
                <input type="color" value={selectedEl.fill || '#ffffff'} onChange={(e) => updateElement(selectedId, { fill: e.target.value })}
                  className="w-full h-[38px] rounded-lg cursor-pointer border border-white/10" />
              </div>
            )}
          </div>
        )}
      </>
    );
  };

  const toolButtons = [
    { id: 'templates', icon: <MagicWand size={20} weight={activePanel === 'templates' ? 'fill' : 'bold'} />, label: 'Templates' },
    { id: 'text', icon: <TextT size={20} weight={activePanel === 'text' ? 'fill' : 'bold'} />, label: 'Text' },
    { id: 'qr', icon: <QrCode size={20} weight={activePanel === 'qr' ? 'fill' : 'bold'} />, label: 'QR' },
    { id: 'bg', icon: <ImageSquare size={20} weight={activePanel === 'bg' ? 'fill' : 'bold'} />, label: 'BG' },
    { id: 'layers', icon: <Palette size={20} weight={activePanel === 'layers' ? 'fill' : 'bold'} />, label: 'Layers' },
  ];

  return (
    <div className="fixed inset-0 z-[100] bg-[#0F1014] flex flex-col" data-testid="qr-design-editor">
      {/* Top Bar */}
      <div className="flex items-center justify-between px-3 md:px-4 py-2 md:py-3 border-b border-white/10 bg-[#15161B]">
        <div className="flex items-center gap-2">
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 text-gray-400 hover:text-white transition-colors" data-testid="editor-close">
            <X size={20} weight="bold" />
          </button>
          <h2 className="text-white font-bold text-sm md:text-lg hidden sm:block">QR Design Editor</h2>
        </div>
        <div className="flex items-center gap-1 md:gap-2">
          <button onClick={undo} disabled={!history.length} className="p-2 rounded-lg hover:bg-white/10 text-gray-400 hover:text-white transition-colors disabled:opacity-30" data-testid="editor-undo" title="Undo">
            <ArrowCounterClockwise size={16} weight="bold" />
          </button>
          <button onClick={redo} disabled={!redoStack.length} className="p-2 rounded-lg hover:bg-white/10 text-gray-400 hover:text-white transition-colors disabled:opacity-30" data-testid="editor-redo" title="Redo">
            <ArrowClockwise size={16} weight="bold" />
          </button>
          <div className="w-px h-5 bg-white/10 mx-0.5 hidden md:block" />
          <button onClick={handleSave} className="flex items-center gap-1 px-3 py-1.5 md:px-4 md:py-2 rounded-xl text-xs md:text-sm font-semibold text-white bg-white/10 hover:bg-white/15 transition-colors" data-testid="editor-save">
            <FloppyDisk size={14} weight="bold" /> <span className="hidden sm:inline">Save</span>
          </button>
          <button onClick={exportImage} className="flex items-center gap-1 px-3 py-1.5 md:px-4 md:py-2 rounded-xl text-xs md:text-sm font-bold text-white transition-all hover:scale-105" style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }} data-testid="editor-export">
            <DownloadSimple size={14} weight="bold" /> <span className="hidden sm:inline">Export</span>
          </button>
        </div>
      </div>

      {/* ===== DESKTOP LAYOUT (md+) ===== */}
      <div className="hidden md:flex flex-1 overflow-hidden">
        {/* Left Sidebar — Tool Icons */}
        <div className="w-14 flex-shrink-0 bg-[#15161B] border-r border-white/10 flex flex-col items-center py-4 gap-1">
          {toolButtons.map(tool => (
            <button key={tool.id} onClick={() => setActivePanel(activePanel === tool.id ? null : tool.id)}
              className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ${activePanel === tool.id ? 'bg-[#B89762]/20 text-[#B89762]' : 'text-gray-500 hover:text-white hover:bg-white/5'}`}
              title={tool.label} data-testid={`tool-${tool.id}`}>
              {tool.icon}
            </button>
          ))}
        </div>

        {/* Left Panel — Context */}
        {activePanel && activePanel !== 'properties' && (
          <div className="w-56 flex-shrink-0 bg-[#1B1C22] border-r border-white/10 p-4 overflow-y-auto">
            {renderPanelContent()}
          </div>
        )}

        {/* Canvas Area */}
        <div className="flex-1 flex items-center justify-center bg-[#0F1014] overflow-auto p-4" ref={containerRef}>
          <div style={{ transform: `scale(${stageScale})`, transformOrigin: 'center center' }}>
            <Stage ref={stageRef} width={CANVAS_W} height={CANVAS_H}
              onClick={handleStageClick} onTap={handleStageClick}
              onDblClick={handleDblClick} onDblTap={handleDblClick}
              style={{ border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', background: '#ffffff' }}>
              <Layer>
                <Rect id="canvas-bg" x={0} y={0} width={CANVAS_W} height={CANVAS_H} fill="#ffffff" />
                {elements.map(el => {
                  if (el.type === 'rect') {
                    return (
                      <Rect key={el.id} id={el.id} x={el.x} y={el.y} width={el.width} height={el.height}
                        fill={el.fill} stroke={el.stroke} strokeWidth={el.strokeWidth}
                        draggable={el.draggable !== false}
                        onClick={() => el.draggable !== false && setSelectedId(el.id)}
                        onTap={() => el.draggable !== false && setSelectedId(el.id)}
                        onDragEnd={(e) => updateElement(el.id, { x: e.target.x(), y: e.target.y() })} />
                    );
                  }
                  if (el.type === 'text') {
                    const textProps = { key: el.id, id: el.id, x: el.x, y: el.y,
                      text: el.text, fontSize: el.fontSize, fontFamily: el.fontFamily, fill: el.fill,
                      draggable: el.draggable !== false,
                      onClick: () => setSelectedId(el.id), onTap: () => setSelectedId(el.id),
                      onDragEnd: (e) => updateElement(el.id, { x: e.target.x(), y: e.target.y() }),
                      onTransformEnd: (e) => {
                        const node = e.target;
                        updateElement(el.id, { x: node.x(), y: node.y(), fontSize: Math.round(el.fontSize * node.scaleX()), scaleX: 1, scaleY: 1 });
                      }
                    };
                    if (el.align === 'center') { textProps.width = CANVAS_W; textProps.align = 'center'; textProps.x = 0; }
                    return <Text {...textProps} />;
                  }
                  if (el.type === 'qr' || el.type === 'image') {
                    return <URLImage key={el.id} el={el} isSelected={selectedId === el.id}
                      onSelect={() => setSelectedId(el.id)}
                      onChange={(attrs) => updateElement(el.id, attrs)} />;
                  }
                  return null;
                })}
                <Transformer ref={transformerRef} boundBoxFunc={(oldBox, newBox) => {
                  if (newBox.width < 20 || newBox.height < 20) return oldBox;
                  return newBox;
                }} />
              </Layer>
            </Stage>
          </div>
        </div>

        {/* Right Panel — Properties (desktop) */}
        <div className="w-56 flex-shrink-0 bg-[#1B1C22] border-l border-white/10 p-4 overflow-y-auto">
          <h3 className="text-white text-sm font-bold mb-3">Properties</h3>
          {selectedEl ? (
            <div className="space-y-4">
              <div className="flex gap-2">
                <button onClick={deleteElement} className="flex-1 py-2 rounded-lg text-xs font-semibold text-red-400 bg-red-500/10 hover:bg-red-500/20 transition-all" data-testid="delete-element">
                  <Trash size={14} weight="bold" className="inline mr-1" /> Delete
                </button>
                <button onClick={() => moveLayer('up')} className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-all"><ArrowUp size={14} weight="bold" /></button>
                <button onClick={() => moveLayer('down')} className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-all"><ArrowDown size={14} weight="bold" /></button>
              </div>
              {selectedEl.type === 'text' && (
                <>
                  <div>
                    <label className="text-gray-400 text-xs mb-1 block">Text</label>
                    <textarea value={selectedEl.text} onChange={(e) => updateElement(selectedId, { text: e.target.value })}
                      className="w-full px-3 py-2 text-sm rounded-lg bg-white/5 border border-white/10 text-white resize-none focus:border-[#B89762]/50 focus:outline-none" rows={2} data-testid="prop-text" />
                  </div>
                  <div>
                    <label className="text-gray-400 text-xs mb-1 block">Font</label>
                    <select value={selectedEl.fontFamily} onChange={(e) => updateElement(selectedId, { fontFamily: e.target.value })}
                      className="w-full px-3 py-2 text-sm rounded-lg bg-white/5 border border-white/10 text-white focus:border-[#B89762]/50 focus:outline-none" data-testid="prop-font">
                      {GOOGLE_FONTS.map(f => <option key={f} value={f}>{f}</option>)}
                    </select>
                  </div>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <label className="text-gray-400 text-xs mb-1 block">Size</label>
                      <input type="number" value={selectedEl.fontSize} onChange={(e) => updateElement(selectedId, { fontSize: Number(e.target.value) })}
                        className="w-full px-3 py-2 text-sm rounded-lg bg-white/5 border border-white/10 text-white focus:border-[#B89762]/50 focus:outline-none" min={8} max={120} data-testid="prop-size" />
                    </div>
                    <div className="flex-1">
                      <label className="text-gray-400 text-xs mb-1 block">Color</label>
                      <input type="color" value={selectedEl.fill} onChange={(e) => updateElement(selectedId, { fill: e.target.value })}
                        className="w-full h-[38px] rounded-lg cursor-pointer border border-white/10" data-testid="prop-color" />
                    </div>
                  </div>
                </>
              )}
              <div>
                <label className="text-gray-400 text-xs mb-1 block">Position</label>
                <div className="flex gap-2">
                  <div className="flex-1"><label className="text-gray-600 text-[10px]">X</label><input type="number" value={Math.round(selectedEl.x)} onChange={(e) => updateElement(selectedId, { x: Number(e.target.value) })} className="w-full px-2 py-1 text-xs rounded-lg bg-white/5 border border-white/10 text-white focus:border-[#B89762]/50 focus:outline-none" /></div>
                  <div className="flex-1"><label className="text-gray-600 text-[10px]">Y</label><input type="number" value={Math.round(selectedEl.y)} onChange={(e) => updateElement(selectedId, { y: Number(e.target.value) })} className="w-full px-2 py-1 text-xs rounded-lg bg-white/5 border border-white/10 text-white focus:border-[#B89762]/50 focus:outline-none" /></div>
                </div>
              </div>
              {(selectedEl.type === 'qr' || selectedEl.type === 'image' || selectedEl.type === 'rect') && selectedEl.width && (
                <div>
                  <label className="text-gray-400 text-xs mb-1 block">Size</label>
                  <div className="flex gap-2">
                    <div className="flex-1"><label className="text-gray-600 text-[10px]">W</label><input type="number" value={Math.round(selectedEl.width)} onChange={(e) => updateElement(selectedId, { width: Number(e.target.value) })} className="w-full px-2 py-1 text-xs rounded-lg bg-white/5 border border-white/10 text-white focus:border-[#B89762]/50 focus:outline-none" /></div>
                    <div className="flex-1"><label className="text-gray-600 text-[10px]">H</label><input type="number" value={Math.round(selectedEl.height)} onChange={(e) => updateElement(selectedId, { height: Number(e.target.value) })} className="w-full px-2 py-1 text-xs rounded-lg bg-white/5 border border-white/10 text-white focus:border-[#B89762]/50 focus:outline-none" /></div>
                  </div>
                </div>
              )}
              {selectedEl.type === 'rect' && (
                <div><label className="text-gray-400 text-xs mb-1 block">Fill Color</label><input type="color" value={selectedEl.fill || '#ffffff'} onChange={(e) => updateElement(selectedId, { fill: e.target.value })} className="w-full h-[38px] rounded-lg cursor-pointer border border-white/10" /></div>
              )}
            </div>
          ) : (
            <div>
              <p className="text-gray-500 text-xs">Select an element to edit</p>
              <div className="mt-6 pt-4 border-t border-white/10">
                <h4 className="text-gray-500 text-[10px] font-semibold uppercase mb-2">Tips</h4>
                <div className="space-y-1 text-[11px] text-gray-600">
                  <p>Double-click text to edit inline</p>
                  <p>Drag corners to resize</p>
                  <p>Use layers panel to reorder</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ===== MOBILE LAYOUT (below md) ===== */}
      <div className="flex md:hidden flex-1 flex-col overflow-hidden relative">
        {/* Canvas Area — full width */}
        <div className="flex-1 flex items-center justify-center bg-[#0F1014] overflow-auto p-2" ref={mobileContainerRef}>
          <div style={{ transform: `scale(${stageScale})`, transformOrigin: 'center center' }}>
            <Stage ref={stageRef} width={CANVAS_W} height={CANVAS_H}
              onClick={handleStageClick} onTap={handleStageClick}
              onDblClick={handleDblClick} onDblTap={handleDblClick}
              style={{ border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', background: '#ffffff' }}>
              <Layer>
                <Rect id="canvas-bg" x={0} y={0} width={CANVAS_W} height={CANVAS_H} fill="#ffffff" />
                {elements.map(el => {
                  if (el.type === 'rect') {
                    return (
                      <Rect key={el.id} id={el.id} x={el.x} y={el.y} width={el.width} height={el.height}
                        fill={el.fill} stroke={el.stroke} strokeWidth={el.strokeWidth}
                        draggable={el.draggable !== false}
                        onClick={() => el.draggable !== false && setSelectedId(el.id)}
                        onTap={() => el.draggable !== false && setSelectedId(el.id)}
                        onDragEnd={(e) => updateElement(el.id, { x: e.target.x(), y: e.target.y() })} />
                    );
                  }
                  if (el.type === 'text') {
                    const textProps = { key: el.id, id: el.id, x: el.x, y: el.y,
                      text: el.text, fontSize: el.fontSize, fontFamily: el.fontFamily, fill: el.fill,
                      draggable: el.draggable !== false,
                      onClick: () => setSelectedId(el.id), onTap: () => setSelectedId(el.id),
                      onDragEnd: (e) => updateElement(el.id, { x: e.target.x(), y: e.target.y() }),
                      onTransformEnd: (e) => {
                        const node = e.target;
                        updateElement(el.id, { x: node.x(), y: node.y(), fontSize: Math.round(el.fontSize * node.scaleX()), scaleX: 1, scaleY: 1 });
                      }
                    };
                    if (el.align === 'center') { textProps.width = CANVAS_W; textProps.align = 'center'; textProps.x = 0; }
                    return <Text {...textProps} />;
                  }
                  if (el.type === 'qr' || el.type === 'image') {
                    return <URLImage key={el.id} el={el} isSelected={selectedId === el.id}
                      onSelect={() => setSelectedId(el.id)}
                      onChange={(attrs) => updateElement(el.id, attrs)} />;
                  }
                  return null;
                })}
                <Transformer ref={transformerRef} boundBoxFunc={(oldBox, newBox) => {
                  if (newBox.width < 20 || newBox.height < 20) return oldBox;
                  return newBox;
                }} />
              </Layer>
            </Stage>
          </div>
        </div>

        {/* Mobile Slide-up Panel */}
        {activePanel && (
          <div className="absolute bottom-14 left-0 right-0 bg-[#1B1C22] border-t border-white/10 rounded-t-2xl max-h-[55%] overflow-y-auto p-4 z-10" style={{ boxShadow: '0 -8px 30px rgba(0,0,0,0.5)' }}>
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-1 rounded-full bg-white/20 mx-auto" />
            </div>
            {renderPanelContent()}
          </div>
        )}

        {/* Mobile Bottom Toolbar */}
        <div className="h-14 flex-shrink-0 bg-[#15161B] border-t border-white/10 flex items-center justify-around px-2">
          {toolButtons.map(tool => (
            <button key={tool.id} onClick={() => setActivePanel(activePanel === tool.id ? null : tool.id)}
              className={`flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-xl transition-all ${activePanel === tool.id ? 'bg-[#B89762]/20 text-[#B89762]' : 'text-gray-500'}`}
              data-testid={`tool-${tool.id}`}>
              {tool.icon}
              <span className="text-[9px] font-medium">{tool.label}</span>
            </button>
          ))}
          {selectedEl && (
            <button onClick={() => setActivePanel(activePanel === 'properties' ? null : 'properties')}
              className={`flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-xl transition-all ${activePanel === 'properties' ? 'bg-[#B89762]/20 text-[#B89762]' : 'text-gray-500'}`}>
              <Trash size={20} weight="bold" />
              <span className="text-[9px] font-medium">Edit</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default QRDesignEditor;
